// [US 6.15] Popup « membre fondateur » — app Android (base installée = acheteurs de l'app).
// Fenêtre d'activation 100 % serveur (GET /api/founders/window) : le client ne décide jamais
// de l'éligibilité, il affiche. Anti-rejeu local : une activation faite (ou un e-mail envoyé)
// pose un flag persistant et le popup ne réapparaît plus (AC 4) ; « Plus tard » = session only.
// Test web : localStorage 'ssiFounderPopupForce' = '1' force l'affichage hors natif — en DÉVELOPPEMENT
// uniquement : actif en prod, il ouvrait le popup (et l'activation par e-mail) sur le site public.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { X, Star, Share2, Mail } from 'lucide-react';
import { authClient } from '@/services/authClient';
import { useAuth } from '@/hooks/useAuth';

const DONE_KEY = 'ssiFounderPopupDone';        // persistant : activé / e-mail envoyé → ne plus afficher
const LATER_KEY = 'ssiFounderPopupLater';      // sessionStorage : « Plus tard » → réapparaît au prochain lancement
const FORCE_KEY = 'ssiFounderPopupForce';      // dev : forcer l'affichage en web

const isEligibleContext = () =>
    Capacitor.isNativePlatform() || (import.meta.env.DEV && localStorage.getItem(FORCE_KEY) === '1');

export const FounderPopup: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { user, loading, refreshUser } = useAuth();
    const [deadline, setDeadline] = useState<string | null>(null);
    const [visible, setVisible] = useState(false);
    const [email, setEmail] = useState('');
    const [busy, setBusy] = useState(false);
    const [status, setStatus] = useState<'idle' | 'sent' | 'activated' | 'error'>('idle');

    useEffect(() => {
        if (!isEligibleContext()) return;
        // Session en cours de restauration : user vaut encore null, un fondateur passerait pour anonyme
        if (loading) return;
        // Fondateur (y compris connecté popup ouvert) : on mémorise ET on referme
        if (user?.founderSince) { localStorage.setItem(DONE_KEY, '1'); setVisible(false); return; }
        if (localStorage.getItem(DONE_KEY) === '1' || sessionStorage.getItem(LATER_KEY) === '1') return;
        authClient.foundersWindow().then(({ open, deadline }) => {
            if (open) { setDeadline(deadline); setVisible(true); }
        });
    }, [user, loading]);

    if (!visible) return null;

    const deadlineLabel = deadline
        ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long' }).format(new Date(deadline))
        : null;

    const dismissLater = () => {
        sessionStorage.setItem(LATER_KEY, '1');
        setVisible(false);
    };

    const markDone = () => localStorage.setItem(DONE_KEY, '1');

    // Connecté : activation immédiate sur le compte. Non connecté : e-mail de confirmation.
    const activate = async () => {
        setBusy(true);
        if (user) {
            const res = await authClient.founderActivateMe();
            if (res.ok) { markDone(); setStatus('activated'); await refreshUser(); }
            else setStatus('error');
        } else {
            const res = await authClient.founderActivate(email.trim(), i18n.language);
            if (res.ok) { markDone(); setStatus('sent'); }
            else setStatus('error');
        }
        setBusy(false);
    };

    // Bouche-à-oreille : partage natif (Capacitor), fallback Web Share API / presse-papiers (dev web).
    const share = async () => {
        const url = Capacitor.isNativePlatform()
            ? 'https://play.google.com/store/apps/details?id=fr.stellarskyimager.planner'
            : 'https://www.stellarskyimager.fr';
        const payload = { title: 'StellarSkyImager', text: t('auth.founder.popup.shareText'), url };
        try {
            if (Capacitor.isNativePlatform()) {
                const { Share } = await import('@capacitor/share');
                await Share.share({ ...payload, dialogTitle: t('auth.founder.popup.share') });
            } else if (navigator.share) {
                await navigator.share(payload);
            } else {
                await navigator.clipboard.writeText(`${payload.text} ${url}`);
            }
        } catch { /* partage annulé : silencieux */ }
    };

    const canSubmit = user ? true : /\S+@\S+\.\S+/.test(email.trim());

    return (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-4" role="dialog" aria-modal="true">
            <div className="relative w-full max-w-md rounded-xl border border-indigo-500/40 bg-slate-900 p-6 shadow-2xl">
                <button onClick={dismissLater} className="absolute right-3 top-3 text-slate-400 hover:text-slate-200" aria-label={t('auth.founder.popup.later')}>
                    <X className="h-5 w-5" />
                </button>

                <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-slate-100">
                    <Star className="h-5 w-5 text-amber-400" /> {t('auth.founder.popup.title')}
                </h2>

                {status === 'activated' || status === 'sent' ? (
                    <div className="space-y-4">
                        <p className="text-sm text-emerald-400">
                            {t(status === 'activated' ? 'auth.founder.popup.activated' : 'auth.founder.popup.sent')}
                        </p>
                        <p className="text-xs text-slate-400">{t('auth.founder.popup.spread')}</p>
                        <div className="flex gap-2">
                            <button onClick={share} className="flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-500">
                                <Share2 className="h-3.5 w-3.5" /> {t('auth.founder.popup.share')}
                            </button>
                            <button onClick={() => setVisible(false)} className="rounded-md bg-slate-700/60 px-3 py-1.5 text-xs font-medium text-slate-200 hover:bg-slate-600/60">
                                {t('auth.founder.popup.close')}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <p className="text-sm text-slate-300">{t('auth.founder.popup.message')}</p>
                        {/* Texte de transparence — wording imposé (jamais « à vie ») */}
                        <p className="text-xs text-slate-400">{t('auth.founder.popup.transparency')}</p>
                        {deadlineLabel && (
                            <p className="text-xs font-medium text-amber-400">{t('auth.founder.popup.deadline', { date: deadlineLabel })}</p>
                        )}

                        {!user && (
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={t('auth.founder.popup.emailPlaceholder')}
                                className="w-full rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-indigo-500 focus:outline-none"
                            />
                        )}

                        {status === 'error' && <p className="text-xs text-red-400">{t('auth.founder.popup.error')}</p>}

                        <div className="flex flex-wrap items-center gap-2 pt-1">
                            <button
                                onClick={activate}
                                disabled={busy || !canSubmit}
                                className="flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
                            >
                                <Mail className="h-3.5 w-3.5" />
                                {t(user ? 'auth.founder.popup.activateMe' : 'auth.founder.popup.activate')}
                            </button>
                            <button onClick={dismissLater} className="rounded-md px-3 py-2 text-xs text-slate-400 hover:text-slate-200">
                                {t('auth.founder.popup.later')}
                            </button>
                            <button onClick={share} className="ml-auto flex items-center gap-1.5 rounded-md bg-slate-700/60 px-3 py-2 text-xs font-medium text-slate-200 hover:bg-slate-600/60">
                                <Share2 className="h-3.5 w-3.5" /> {t('auth.founder.popup.share')}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
